// src/components/author/SubmissionWizard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronLeft, 
  Lock, 
  FileUp, 
  Type, 
  AlignLeft, 
  Hash, 
  Users, 
  Eye, 
  CheckCircle,
  Check,
  Save,
  AlertCircle,
  Loader
} from 'lucide-react';

import ArticleType from './wizard/ArticleType';
import UploadManuscript from './wizard/UploadManuscript';
import Title from './wizard/Title';
import Abstract from './wizard/Abstract';
import Keywords from './wizard/Keywords';
import Authors from './wizard/Authors';
import Reviewers from './wizard/Reviewers';
import Disclosures from './wizard/Disclosures';
import ReviewSubmit from './wizard/ReviewSubmit';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import HeaderMessages from '../common/HeaderMessages';
import { showToast } from '../../utils/toast';
import { getBaseManuscriptId } from '../../utils/submissionUtility';

const steps = [
  { id: 1, name: 'Article Type', icon: Lock, component: ArticleType, messageId:0, headerMessage: HeaderMessages[0] },
  { id: 2, name: 'Upload Manuscript', icon: FileUp, component: UploadManuscript, messageId:1, headerMessage: HeaderMessages[1] },
  { id: 3, name: 'Title', icon: Type, component: Title, messageId:2, headerMessage: HeaderMessages[2] },
  { id: 4, name: 'Abstract', icon: AlignLeft, component: Abstract, messageId:3, headerMessage: HeaderMessages[3] },
  { id: 5, name: 'Keywords', icon: Hash, component: Keywords, messageId:4, headerMessage: HeaderMessages[4] },
  { id: 6, name: 'Authors', icon: Users, component: Authors, messageId:5, headerMessage: HeaderMessages[5] },
  { id: 7, name: 'Reviewers', icon: Eye, component: Reviewers, messageId:6, headerMessage: HeaderMessages[6] },
  { id: 8, name: 'Disclosures', icon: CheckCircle, component: Disclosures, messageId:7, headerMessage: HeaderMessages[7] },
  { id: 9, name: 'Review & Submit', icon: CheckCircle, component: ReviewSubmit, messageId:8, headerMessage: HeaderMessages[8] },
];

// Scroll an element to the top of the wizard's scroll container (the layout's <main>
// element), leaving a little breathing room above it. The author layout scrolls inside
// <main>, so a plain window.scrollTo would have no effect here.
const scrollElementIntoView = (el, offset = 16) => {
  if (!el) return;

  const scroller = el.closest('main');
  if (scroller) {
    const scrollerRect = scroller.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = scroller.scrollTop + (elRect.top - scrollerRect.top) - offset;
    scroller.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    return;
  }

  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
};

const SubmissionWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refs used to bring the current step's header/instructions into view and to keep the
  // active step visible inside the horizontally scrollable step strip on mobile.
  const stepHeaderRef = useRef(null);
  const stepsNavRef = useRef(null);
  const activeStepButtonRef = useRef(null);
  const hasMountedRef = useRef(false);
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get('a');
  const previousManuscriptIdQuery = queryParams.get('pid');
  const [manuscriptId, setManuscriptId] = useState(editId);
  const manuscriptIdRef = useRef(editId);

  const isCorrection = queryParams.get('correction') === 'true';
  const isRevision = queryParams.get('revision') === 'true';
  const statusParam = queryParams.get('status');

  const previousId = getBaseManuscriptId(previousManuscriptIdQuery || editId, isRevision ? 'revision' : isCorrection ? 'correction' : null);
  const revisionId = isRevision ? queryParams.get('rid') : null;

  const status = statusParam || (isCorrection ? 'correction' : isRevision ? 'revision' : 'new');
  const submissionEndpoint = status === "revision" ? `/api/authors/submit-revision` : status === "correction" ? `/api/authors/submit-correction` : `/api/authors/submit-manuscript`;
  // const submissionEndpoint = `/api/authors/submit-manuscript/`
  const [newSubmissionId, setNewSubmissionID] = useState(editId)

  // fetch new Id from database 
//   useEffect(() => {
//     const loadNewId = async () => {
//     try{
//       const params = {
//         revision: isRevision ? `&revise=true` : "",
//         correction: isCorrection? `&correct=true` : ""
//       }
//       const response = await axios.get(`/api/authors/generate-submission-id?a=${previousId}${params.correction}${params.revision}`);
//       setNewSubmissionID(response.data.id);
//       setManuscriptId(response.data.id)
//       manuscriptIdRef.current = response.data.id;
//     }catch(error){
//       console.error(error)
//     }
//   }
// if(editId && (isCorrection || isRevision) && !statusParam){
//   loadNewId()
// }
// }, [editId])

  const [formData, setFormData] = useState({
    articleType: '',
    discipline: '',
    previousSubmission: isCorrection || isRevision ? 'yes' : 'no',
    previousId: revisionId ? revisionId : previousId || editId || '',
    files: {
      manuscript: null,
      coverLetter: null,
      tables: null,
      figures: null,
      supplementary: null,
      trackedManuscript: null,
      graphicAbstract: null
    },
    title: '',
    abstract: '',
    keywords: ['', '', '', '', '', '', '', ''],
    authors: [],
    reviewers: [],
    disclosures: {
      d1: false,
      d2: false,
      d3: false,
      d4: false,
      d5: false,
      d6: false,
      d7: false,
      d8: false,
    },
    isWomenInScience: '',
    isBelispointAcademic: '',
    isKidnappingForRansom: '',
  });

  const [completedSteps, setCompletedSteps] = useState({});

  // Load existing submission if editing
useEffect(() => {
  const loadSubmission = async () => {
    if (editId) {
      setLoading(true);
      try {
        const sentId = isCorrection ? previousId : revisionId || editId;
        // const sentId = editId;
        const response = await axios.get(`/api/authors/submission/${sentId}?pid=${editId}`);
        if (response.data.status === 'success') {
          const data = response.data.submission;
          if(data.status !== "draft" && data.status !== "correction_draft" && data.status !== "revision_draft" && data.status !== "saved" && data.status !== "correction_saved" && data.status !== "revision_saved"&& data.status !== "returned_for_revision" && data.status !== "returned_for_correction"){
            showToast(`This submission has already been submitted and cannot be edited. Redirecting to submission details.${data.status}`, 'error');
       
            // navigate(`/manuscripts?a=${data.manuscriptId}`);
            return;
          }
          // Merge the loaded data with existing form data
          setFormData(prev => ({
            ...prev,
            ...data,
            // Ensure these fields are properly set if they come from the API
            previousSubmission: data.previousId || isCorrection || isRevision ? 'yes' : 'no',
            // previousId: isCorrection || isRevision ? data.previousId || editId || '' : '',
            previousId: revisionId ? revisionId : previousId || data.previousId 

          }));
          
          if(!isCorrection && !isRevision){
            setManuscriptId(data.manuscriptId);
            manuscriptIdRef.current = data.manuscriptId;
          }
          
          // Mark steps as completed based on loaded data
          const completed = {};
          if (data.articleType) completed[1] = true;
          if (data.files?.manuscript) completed[2] = true;
          if (data.title) completed[3] = true;
          if (data.abstract) completed[4] = true;
          if (data.keywords?.filter(k => k).length >= 3) completed[5] = true;
          if (data.authors?.length > 0) completed[6] = true;
          setCompletedSteps(completed);
        }
      } catch (error) {
        console.error('Error loading submission:', error);
        setError('Failed to load submission data');
      } finally {
        setLoading(false);
      }
    }
  };

  loadSubmission();
}, [editId, isCorrection, isRevision, newSubmissionId, status]);

  const CurrentStepComponent = steps.find(step => step.id === currentStep)?.component;
const currentHeaderMessage = steps.find(step => step.id === currentStep)?.headerMessage;
  // Files are uploaded the moment they are picked, so the form should only ever hold
  // their URLs. Anything still a File object has not finished uploading.
  const hasPendingFileUploads = () =>
    Object.values(formData.files || {}).some(file => file instanceof File);

  // Save current step data to database (JSON metadata + already-uploaded file URLs).
  // `force` is used before the first upload, which needs a row to attach to.
  const saveCurrentStep = useCallback(async ({ force = false } = {}) => {
    if (!force && !hasUnsavedChanges) return true;

    setSaving(true);
    setError('');
    
    try {
      const payload = {
        articleType: formData.articleType,
        files: formData.files,
        discipline: formData.discipline,
        previousSubmission: formData.previousSubmission,
        previousId: formData.previousId || '',
        title: formData.title || '',
        abstract: formData.abstract || '',
        isWomenInScience: formData.isWomenInScience,
        isBelispointAcademic: formData.isBelispointAcademic,
        isKidnappingForRansom: formData.isKidnappingForRansom,
        keywords: formData.keywords.filter(k => k && k.trim() !== ''),
        authors: formData.authors,
        reviewers: formData.reviewers,
        disclosures: formData.disclosures
      };

      if (manuscriptIdRef.current) {
        payload.manuscriptId = manuscriptIdRef.current;
      }

      // Determine action type
      if (isCorrection) {
        payload.action = 'correction_saved';
      } else if (isRevision) {
        payload.action = 'revision_saved';
      } else {
        payload.action = 'draft';
      }
      

      const response = await axios.post(`${submissionEndpoint}/draft`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        // Revisions and corrections mint a new suffixed row on first save, so always
        // adopt the id the server reports — later saves (and file uploads) then target
        // that row instead of the original manuscript.
        if (response.data.manuscriptId && response.data.manuscriptId !== manuscriptIdRef.current) {
          setManuscriptId(response.data.manuscriptId);
          manuscriptIdRef.current = response.data.manuscriptId;
        }
        setHasUnsavedChanges(false);
        showToast('Progress saved successfully!', 'success');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving draft:', error);
      showToast(error.response?.data?.message || 'Failed to save progress. Please try again.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [formData, isCorrection, isRevision, hasUnsavedChanges, submissionEndpoint]);

  // Uploads are useless without a submission to attach them to, so make sure the
  // draft exists first — a brand new wizard may not have saved anything yet.
  const ensureManuscriptId = useCallback(async () => {
    if (manuscriptIdRef.current) return manuscriptIdRef.current;

    const saved = await saveCurrentStep({ force: true });
    return saved ? manuscriptIdRef.current : null;
  }, [saveCurrentStep]);

  const handleNext = async () => {
    // Validate current step first
    const stepComponent = steps.find(s => s.id === currentStep)?.component;
    if (stepComponent && typeof stepComponent.validate === 'function') {
      const isValid = stepComponent.validate(formData);
      if (!isValid) return;
    }

    // Files upload as soon as they are picked, so a File instance here means an
    // upload is still in flight (or never landed) — refuse to advance.
    if (hasPendingFileUploads()) {
      showToast('Please wait for your files to finish uploading', 'error');
      return;
    }

    // Save current step data
    const saved = await saveCurrentStep();
    if (saved) {
      setCompletedSteps(prev => ({ ...prev, [currentStep]: true }));
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = async (stepId) => {
    // Only allow going to steps that are completed or current
    if (stepId <= currentStep || completedSteps[stepId - 1]) {
      // Save current step if there are unsaved changes
      if (hasUnsavedChanges) {
        const saved = await saveCurrentStep();
        if (saved) {
          setCurrentStep(stepId);
        }
      } else {
        setCurrentStep(stepId);
      }
    }
  };

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...(typeof data === 'function' ? data(prev) : data) }));
    setHasUnsavedChanges(true);
    setError('');
  };

  const isStepCompleted = (stepId) => {
    return completedSteps[stepId];
  };

  const isStepAccessible = (stepId) => {
    return stepId <= currentStep || completedSteps[stepId - 1];
  };

  const saveDraft = async () => {
    await saveCurrentStep();
  };

  // Whenever the wizard lands on a step (Save & Continue, Back or a step click), bring
  // that step's header and instructions ("Step 2: Upload Manuscript") into view.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    scrollElementIntoView(stepHeaderRef.current);

    // Keep the active step visible in the horizontal step strip (mobile/tablet).
    const nav = stepsNavRef.current;
    const activeButton = activeStepButtonRef.current;
    if (nav && activeButton && nav.scrollWidth > nav.clientWidth) {
      const navRect = nav.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      nav.scrollTo({
        left: nav.scrollLeft + (buttonRect.left - navRect.left) - (nav.clientWidth - buttonRect.width) / 2,
        behavior: 'smooth',
      });
    }
  }, [currentStep]);

  const submitManuscript = async () => {
    // Validate all required steps are completed
    const requiredSteps = [1, 2, 3, 4, 5, 6, 8]; // Steps that are required
    const allRequiredCompleted = requiredSteps.every(step => completedSteps[step]);
    
    if (!allRequiredCompleted) {
      showToast('Please complete all required steps before submitting', 'error');
      return;
    }

    setSubmitting(true);
    setError('');
    
    // Show overlay
    const overlay = document.querySelector('.overlayX');
    if (overlay) overlay.style.display = 'flex';

    try {
      // 1) Save metadata + the file URLs collected while the author uploaded them
      if (hasUnsavedChanges) {
        const saved = await saveCurrentStep();
        if (!saved) {
          if (overlay) overlay.style.display = 'none';
          return;
        }
      }

      // 2) Every file must already be on the server before we finalize
      if (hasPendingFileUploads()) {
        if (overlay) overlay.style.display = 'none';
        showToast('Please wait for your files to finish uploading before submitting', 'error');
        return;
      }

      // 3) Finalize (JSON): flips status to submitted and sends emails
      const action = isCorrection ? 'correction_submitted' : isRevision ? 'revision_submitted' : 'submit';
      const finalizeResponse = await axios.post(`${submissionEndpoint}/finalize`, {
        manuscriptId: manuscriptIdRef.current,
        action
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (overlay) overlay.style.display = 'none';

      if (finalizeResponse.data.status === 'success') {
        showToast(finalizeResponse.data?.message || 'Manuscript submitted successfully!', 'success');
        navigate('/manuscripts');
      } else {
        showToast(finalizeResponse.data?.message || 'Failed to submit manuscript. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error submitting manuscript:', error);
      if (overlay) overlay.style.display = 'none';
      showToast(error.response?.data?.message || 'Failed to submit manuscript. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Warn user about unsaved changes when leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading submission data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl w-full mx-auto px-4 py-8">
      {/* Upload Overlay */}
      <div className="overlayX hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <div className="font-bold text-black">Submitting Manuscript...please wait...</div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800">
          {isCorrection ? 'Submit Correction' : isRevision ? 'Submit Revision' : 'Submit New Manuscript'}
        </h2>
        <p className="text-gray-600 mt-2">
          {isCorrection ? 'Submit a corrected version of your manuscript' : 
           isRevision ? 'Submit a revised version of your manuscript' : 
           'Follow the steps below to submit your manuscript'}
        </p>
       
        {manuscriptId && (
          <p className="text-sm text-purple-600 mt-2">Manuscript ID: {manuscriptId}</p>
        )}
      </div>

      {/* Auto-save indicator */}
      {saving && (
        <div className="mb-6 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
          <Loader className="w-4 h-4 animate-spin text-blue-600 mr-2" />
          <p className="text-sm text-blue-700">Saving your progress...</p>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="flex flex-col md:flex-row lg:flex-row gap-6">
        {/* Left Column - Step Navigation */}
        <div className="w-full md:w-60 lg:w-80 md:flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm md:sticky md:top-24">
            {/* Progress Header */}
            <div className="p-4 md:p-6 border-b border-gray-100">
              <div className="flex items-center justify-between gap-3 md:block">
                <h3 className="font-semibold text-gray-800">Submission Progress</h3>
                <p className="text-sm text-gray-500 md:mt-1">
                  Step {currentStep} of {steps.length}
                </p>
              </div>
              {/* Progress Bar */}
              <div className="mt-3 md:mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-600 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Steps List - vertical on md+, horizontal & scrollable on phones */}
            <nav
              ref={stepsNavRef}
              className="p-3 md:p-4 flex md:block gap-2 md:gap-0 overflow-x-auto md:overflow-visible no-scrollbar"
            >
              {steps.map((step) => {
                const isActive = step.id === currentStep;
                const isCompleted = isStepCompleted(step.id);
                const isAccessible = isStepAccessible(step.id);
                
                return (
                  <button
                    key={step.id}
                    ref={isActive ? activeStepButtonRef : null}
                    onClick={() => goToStep(step.id)}
                    disabled={!isAccessible}
                    className={`w-auto md:w-full flex-shrink-0 md:flex-shrink flex items-center p-3 md:p-4 rounded-lg md:mb-2 transition-all ${
                      isActive 
                        ? 'bg-purple-50 border-2 border-purple-200' 
                        : isCompleted
                        ? 'hover:bg-gray-50'
                        : isAccessible
                        ? 'hover:bg-gray-50'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {/* Step Icon */}
                    <div className={`relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>

                    {/* Step Info */}
                    <div className="ml-3 md:ml-4 flex-1 text-left">
                      <p className={`hidden md:block text-sm font-medium ${
                        isActive ? 'text-purple-700' : 'text-gray-700'
                      }`}>
                        Step {step.id}
                      </p>
                      <p className={`text-xs whitespace-nowrap ${
                        isActive ? 'text-purple-600' : 'text-gray-500'
                      }`}>
                        {step.name}
                      </p>
                    </div>

                    {/* Lock Icon for inaccessible steps */}
                    {!isAccessible && (
                      <Lock className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Draft Save Button */}
            <div className="p-3 md:p-4 border-t border-gray-100">
              <button
                onClick={saveDraft}
                disabled={saving || !hasUnsavedChanges}
                className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {hasUnsavedChanges ? 'Save Progress' : 'All Saved'}
                  </>
                )}
              </button>
            </div>

            {/* Help Section */}
            <div className="hidden md:block p-6 border-t border-gray-100 bg-gray-50 rounded-b-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Need Help?</h4>
              <p className="text-xs text-gray-500 mb-3">
                Can't complete a step? Make sure all required fields are filled.
              </p>
              <a 
                href="/support" 
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Contact Support →
              </a>
            </div>
          </div>
        </div>

        {/* Right Column - Form Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8">
            {/* Step Header */}
            <div ref={stepHeaderRef} className="mb-6 pb-6 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800">
                Step {currentStep}: {steps.find(s => s.id === currentStep)?.name}
              </h3>
              {hasUnsavedChanges && (
                <p className="text-xs text-amber-600 mt-2 flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  You have unsaved changes
                </p>
              )}
            </div>
           <div>
      {currentHeaderMessage && (
        <div className="mb-6 p-6 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl shadow-sm">
          {currentHeaderMessage}
        </div>
      )}
    </div>
            {/* Current Step Component */}
            <CurrentStepComponent
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
              currentStep={currentStep}
              totalSteps={steps.length}
              isCorrection={isCorrection}
              isRevision={isRevision}
              manuscriptId={manuscriptId}
              ensureManuscriptId={ensureManuscriptId}
              saving={saving}
            />
          </div>

          {/* Navigation Buttons for steps that don't have them built-in */}
          {![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(currentStep) && (
            <div className="flex justify-between mt-6">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`flex items-center px-6 py-3 rounded-lg transition-all ${
                  currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              
              <button
                onClick={handleNext}
                disabled={saving}
                className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    Save & Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Final Submit Button for Review Step */}
          {currentStep === 9 && (
            <div className="flex justify-end mt-6">
              <button
                onClick={submitManuscript}
                disabled={submitting || saving}
                className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Submit Manuscript
                  </>
                )}
              </button>
            </div>
          )}

          {/* Progress Indicator for Mobile */}
          <div className="mt-4 text-center text-sm text-gray-500 lg:hidden">
            Step {currentStep} of {steps.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionWizard;